<template>
  <div v-if="!loading" class="content">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <n-button type="info" class="mt-0 px-0" link @click.native="back">
            <i class="bx bx-chevron-left" /> View all offers
          </n-button>
        </div>
        <div class="col-xl-8 col-lg-7">
          <div class="row align-items-center">
            <div class="col-auto">
              <img
                :src="offer.locationLogo"
                class="img img-raised rounded logo"
              />
            </div>
            <div class="col">
              <div class="row align-items-center">
                <div class="col-lg-12 col-auto">
                  <h3 class="mb-2">{{ offer.locationName }}</h3>
                </div>
                <div class="col-auto">
                  <h6 class="category text-primary">{{ offer.name }}</h6>
                </div>
                <div v-if="offer.reviewSummary" class="col-auto">
                  <star-rating
                    v-model="offer.reviewSummary.averageStars"
                    :increment="0.1"
                    :show-rating="false"
                    :star-size="20"
                    :inline="true"
                    :read-only="true"
                  />
                </div>
                <div v-if="offer.reviewSummary" class="col pl-0">
                  ({{ offer.reviewSummary ? offer.reviewSummary.total : 0 }}
                  Reviews)
                </div>
              </div>
              <div class="row d-none d-md-flex">
                <div
                  v-for="amenitie in offer.amenities"
                  :key="amenitie.name"
                  class="col-lg-auto col-md-6"
                >
                  <div class="amenitie">
                    <i :class="amenitie.icon" class="bx mr-1" />{{
                      amenitie.name
                    }}
                  </div>
                </div>
              </div>
              <div v-if="offer.coordinate" class="row">
                <div class="col-12">
                  <a
                    :href="`http://maps.google.com/maps?q=${offer.coordinate.coordinates[1]},${offer.coordinate.coordinates[0]}`"
                    target="_blank"
                    class="text-info"
                  >
                    {{ offer.address }}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="row d-flex d-md-none py-2">
            <div
              v-for="amenitie in offer.amenities"
              :key="amenitie.name"
              class="col-sm-auto"
            >
              <div class="amenitie">
                <i :class="amenitie.icon" class="bx mr-1" />{{ amenitie.name }}
              </div>
            </div>
          </div>
          <div class="row my-3">
            <div class="col-md-12">
              <VueSlickCarousel
                ref="image"
                :arrows="false"
                :as-nav-for="$refs.thumbs"
                :focus-on-select="true"
              >
                <div
                  v-for="(image, index) in offer.images"
                  :key="index"
                  :style="{ backgroundImage: 'url(' + image + ')' }"
                  class="carousel-item"
                />
              </VueSlickCarousel>
              <VueSlickCarousel
                ref="thumbs"
                :arrows="false"
                :as-nav-for="$refs.image"
                :slides-to-show="5"
                :focus-on-select="true"
              >
                <div v-for="(image, index) in offer.images" :key="index">
                  <div
                    :style="{ backgroundImage: 'url(' + image + ')' }"
                    class="carousel-thumb-item"
                  />
                </div>
              </VueSlickCarousel>
            </div>
          </div>

          <div class="d-block d-md-none form-reserve">
            <h4 class="mt-0">Check In / Check Out</h4>
            <APCDateRangePicker
              :initial-range="initialRange"
              :max-length="options.maxReservationDays"
              :captions-of-date="dateInfoExample"
              @update="updateRange"
            />
            <div v-if="offer.cost">
              <div class="row align-items-center mb-1">
                <div class="col-6">Length:</div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ `${offer.cost.dailyRateDayCount} ${offer.cost.dailyRateDayCount > 1 ? 'Days' : 'Day'}` }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-12">
                  <el-collapse accordion>
                    <el-collapse-item name="1">
                      <template slot="title">
                        <div class="col-6">
                          <i class="bx bx-plus mr-1" />
                          <i class="bx bx-minus mr-1" />

                          Avg Daily Rate:
                        </div>
                        <div class="col-6 pl-0">
                          <h5 class="font-weight-normal m-0">
                            ${{ offer.cost.averageDailyRate }}
                          </h5>
                        </div>
                      </template>
                      <ul v-if="offer.cost && offer.cost.dailyRates">
                        <li v-for="rt in offer.cost.dailyRates" :key="'rt_' + rt.date">
                          <div class="row align-items-center">
                            <div class="col-6">{{ rt.date }}:</div>
                            <div class="col-6 pl-0">
                              <h5 class="font-weight-normal m-0">
                                ${{ rt.rate }}
                              </h5>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </el-collapse-item>
                  </el-collapse>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">Total Daily:</div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">
                    ${{ offer.cost.totalDailyRate }}
                  </h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Processing Fee<i
                    class="bx bx-help-circle bx-xs help-icon"
                    @click="openTip('Processing Fee', processingFeeContent)"
                  />:
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">
                    ${{ (offer.cost.appliedProcessingFee ? offer.cost.appliedProcessingFee.amount : 0) }}
                  </h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-12">
                  <el-collapse accordion>
                    <el-collapse-item name="1">
                      <template slot="title">
                        <div class="col-6">
                          <i class="bx bx-plus mr-1" />
                          <i class="bx bx-minus mr-1" />

                          Location Fees:
                        </div>
                        <div class="col-6 pl-0">
                          <h5 class="font-weight-normal m-0">
                            ${{ offer.cost.totalFees }}
                          </h5>
                        </div>
                      </template>
                      <ul v-if="offer.cost.fees">
                        <li v-for="fee in offer.cost.fees" :key="fee.id">
                          <div class="row align-items-center">
                            <div class="col-6">{{ fee.description }}:</div>
                            <div class="col-6 pl-0">
                              <h5 class="font-weight-normal m-0">
                                ${{ fee.amount }}
                              </h5>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </el-collapse-item>
                  </el-collapse>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-12">
                  <el-collapse accordion>
                    <el-collapse-item name="1">
                      <template slot="title">
                        <div class="col-6">
                          <i class="bx bx-plus mr-1" />
                          <i class="bx bx-minus mr-1" />
                          Discounts:
                        </div>
                        <div class="col-6 pl-0">
                          <h5 class="font-weight-normal m-0">
                            {{ `${offer.cost.totalDiscounts ? '-' : ''} $${offer.cost.totalDiscounts || 0}` }}
                          </h5>
                        </div>
                      </template>
                      <ul v-if="offer.cost.discounts">
                        <li v-for="discount in offer.cost.discounts" :key="discount.id">
                          <div class="row align-items-center">
                            <div class="col-6">{{ discount.description }}:</div>
                            <div class="col-6 pl-0">
                              <h5 class="font-weight-normal m-0">
                                {{ `${discount.amount ? '-' : ''} $${discount.amount || 0}` }}
                              </h5>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </el-collapse-item>
                  </el-collapse>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">Total:</div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-bold m-0">
                    ${{ offer.cost.totalCost }}
                  </h5>
                </div>
              </div>
            </div>
            <div class="row align-items-center mb-1">
              <div class="col-auto">Coupon Code:</div>
              <div class="col pl-0">
                <input
                  :value="couponCode"
                  type="text"
                  placeholder="Coupon Code"
                  class="form-control"
                  addon-left-icon="now-ui-icons users_circle-08"
                  @input="
                    setCouponCode($event.target.value)
                  "
                />
              </div>
              <div class="col-auto">
                <n-button
                  :disabled="!couponCode"
                  native-type="button"
                  type="success"
                  block
                  size="sm"
                  @click.native="applyCouponCode"
                >
                  Apply
                </n-button>
              </div>
            </div>
            <h4 class="mb-1">Customer Info</h4>
            <ValidationObserver v-slot="{ handleSubmit }" ref="reserveForm">
              <form class="form" @submit.prevent="handleSubmit(onReserve)">
                <ValidationProvider name="Email" rules="required|email">
                  <div slot-scope="{ errors }">
                    <label>Email</label>
                    <fg-input
                      :value="customer.email"
                      :readonly="accessToken ? true : false"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons ui-1_email-85"
                      placeholder="Email"
                      @input="setCustomerInfo('email', $event.target.value)"
                    />
                  </div>
                </ValidationProvider>
                <ValidationProvider
                  v-if="!accessToken"
                  :rules="{
                    required: accessToken ? false : true,
                    min: accessToken ? 0 : 6,
                  }"
                  name="Password"
                >
                  <div slot-scope="{ errors }">
                    <label>Password</label>
                    <fg-input
                      :value="customer.password"
                      :error="errors[0]"
                      type="password"
                      addon-left-icon="now-ui-icons ui-1_lock-circle-open"
                      placeholder="Password"
                      @input="setCustomerInfo('password', $event.target.value)"
                    />
                  </div>
                </ValidationProvider>
                <div class="row">
                  <div class="col-md-6">
                    <ValidationProvider name="First Name" rules="required">
                      <div slot-scope="{ errors }">
                        <label>First Name</label>
                        <fg-input
                          :value="customer.firstName"
                          :readonly="accessToken ? true : false"
                          :error="errors[0]"
                          placeholder="First Name"
                          addon-left-icon="now-ui-icons users_circle-08"
                          @input="
                            setCustomerInfo('firstName', $event.target.value)
                          "
                        />
                      </div>
                    </ValidationProvider>
                  </div>
                  <div class="col-md-6">
                    <ValidationProvider name="Last Name" rules="required">
                      <div slot-scope="{ errors }">
                        <label>Last Name</label>
                        <fg-input
                          :value="customer.lastName"
                          :readonly="accessToken ? true : false"
                          :error="errors[0]"
                          placeholder="Last Name"
                          addon-left-icon="now-ui-icons users_circle-08"
                          @input="
                            setCustomerInfo('lastName', $event.target.value)
                          "
                        />
                      </div>
                    </ValidationProvider>
                  </div>
                </div>
                <ValidationProvider
                  :rules="{ required: true, regex: phoneRegex }"
                  name="Phone"
                >
                  <div slot-scope="{ errors }">
                    <label>Phone</label>
                    <fg-input
                      :value="customer.phoneNumber"
                      :readonly="accessToken ? true : false"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons tech_mobile"
                      placeholder="Phone"
                      @input="setCustomerInfo('phoneNumber', $event.target.value)"
                    />
                  </div>
                </ValidationProvider>
                <h4>
                  Payment
                  <i
                    class="bx bx-help-circle bx-sm help-icon"
                    @click="openTip('Payment', paymentOptions)"
                  />
                </h4>
                <!-- <card v-if="validCard">
                  <div class="row align-items-center">
                    <div class="col-auto pr-0">
                      <img
                        :src="'img/card-icons/' + customer.cardsOnFile.cardType.toLowerCase() + '.svg'"
                        class="img cc-icon"
                      />
                    </div>
                    <div class="col-auto cc-number">
                      **** **** ****
                      {{ customer.cardsOnFile.lastFour.substr(4) }}
                    </div>
                    <div class="col cc-expire">
                      Expire: {{ customer.cardsOnFile.expirationDate }}
                    </div>
                  </div>
                </card>
                <card @click.native="openCardModal = true">
                  <div class="row align-items-center">
                    <div class="col-auto pr-0">
                      <img
                        src="img/card-icons/default.svg"
                        class="img cc-icon"
                      />
                    </div>
                    <div class="col-auto cc-number">
                      {{ validCard ? "Change" : "Add" }} Card
                    </div>
                  </div>
                </card> -->
                <div class="card-wrapper">
                  <div>
                    <label>Billing Zip</label>
                    <fg-input
                      :value="zip"
                      :error="errZip"
                      placeholder="Billing Zip"
                      @input="zip=$event.target.value"
                      @blur="validateZip"/>
                  </div>
                  <div>
                    <label>Card Number</label>
                    <fg-input
                      v-cardformat:formatCardNumber
                      ref="cardNumInput"
                      :value="cardNumber"
                      :error="errCardNumber"
                      :autocomplete="'cc-number'"
                      placeholder="#### #### #### ####"
                      @input="cardNumber=$event.target.value"
                      @blur="validateCardNumber">
                      <div 
                        slot="addonLeft" 
                        class="card-icon-wrapper">
                        <img 
                          :src="'img/card-icons/' + getIcon() + '.svg'"
                          height="18">
                      </div>
                    </fg-input>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <label>Expiration</label>
                      <fg-input
                        v-cardformat:formatCardExpiry
                        ref="cardExpInput"
                        :value="cardExpiry"
                        :error="errCardExpiry"
                        :autocomplete="'cc-exp'"
                        placeholder="Expiration"
                        @input="cardExpiry=$event.target.value"
                        @blur="validateCardExpiry"/>
                    </div>
                    <div class="col-6">
                      <label>CVC</label>
                      <fg-input
                        v-cardformat:formatCardCVC
                        ref="cardCvcInput"
                        :value="cardCvc"
                        :error="errCardCvc"
                        :autocomplete="'cc-csc'"
                        placeholder="CVC"
                        @input="cardCvc=$event.target.value"
                        @blur="validateCardCVC"/>
                    </div>
                  </div>
                </div>
                <n-button
                  :disabled="saving"
                  native-type="submit"
                  type="danger"
                  block
                >
                  {{ saving ? "" : "Book Reservation" }}
                  <i v-if="saving" class="bx bx-loader bx-spin" />
                </n-button>
                <img src="/img/authorize-net-seal.png" width="100%" class="my-3">
              </form>
            </ValidationObserver>
          </div>

          <!-- Details Start -->
          <div v-if="offer.sections.length" class="row">
            <div class="col-12">
              <h4 class="mt-0">Details</h4>
              <collapse :active-index="0">
                <collapse-item
                  v-for="(section, index) in offer.sections"
                  :key="index"
                  :title="section.name"
                >
                  <template v-slot:title>
                    {{ section.name }}
                  </template>
                  <template v-slot:icon>
                    <i class="bx bx-plus mr-1" />
                    <i class="bx bx-minus mr-1" />
                  </template>
                  <div v-html="section.text" />
                </collapse-item>
              </collapse>
            </div>
          </div>
          <!-- Details End -->

          <!-- Reviews Start -->
          <!-- <div v-if="offer.reviewSummary" class="row d-md-block d-none">
            <div class="col-12">
              <h4>Reviews</h4>
              <VueSlickCarousel
                :arrows="reviewArrows"
                :as-nav-for="$refs.thumbs"
                :focus-on-select="true"
                :slides-to-show="reviewShows"
              >
                <card
                  v-for="review in offer.reviewSummary.reviews"
                  :key="review.id"
                  type="testimonial"
                  plain
                  @click.native="openReview(review)"
                >
                  <h5 class="card-title">{{ review.reviewerName }}</h5>
                  <star-rating
                    v-model="review.stars"
                    :increment="0.1"
                    :show-rating="false"
                    :star-size="16"
                    :inline="true"
                    :read-only="true"
                  />
                  <p class="card-description">"{{ review.reviewText }}"</p>
                  <div class="review-date">
                    {{ dateFormat(review.reviewDate) }}
                  </div>
                </card>
              </VueSlickCarousel>
            </div>
          </div> -->
          <!-- Reviews End -->
        </div>
        <div class="col-xl-4 col-lg-5">
          <div class="d-none d-md-block">
            <h4 class="mt-0">Check In / Check Out</h4>
            <APCDateRangePicker
              :initial-range="initialRange"
              :max-length="options.maxReservationDays"
              :captions-of-date="dateInfoExample"
              @update="updateRange"
            />
            <div v-if="offer.cost">
              <div class="row align-items-center mb-1">
                <div class="col-6">Length:</div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">{{ `${offer.cost.dailyRateDayCount} ${offer.cost.dailyRateDayCount > 1 ? 'Days' : 'Day'}` }}</h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-12">
                  <el-collapse accordion>
                    <el-collapse-item name="1">
                      <template slot="title">
                        <div class="col-6">
                          <i class="bx bx-plus mr-1" />
                          <i class="bx bx-minus mr-1" />
                          Avg Daily Rate:
                        </div>
                        <div class="col-6 pl-0">
                          <h5 class="font-weight-normal m-0">
                            ${{ offer.cost.averageDailyRate }}
                          </h5>
                        </div>
                      </template>
                      <ul v-if="offer.cost && offer.cost.dailyRates">
                        <li v-for="rt in offer.cost.dailyRates" :key="'rt_' + rt.date">
                          <div class="row align-items-center">
                            <div class="col-6">{{ rt.date }}:</div>
                            <div class="col-6 pl-0">
                              <h5 class="font-weight-normal m-0">
                                ${{ rt.rate }}
                              </h5>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </el-collapse-item>
                  </el-collapse>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">Total Daily:</div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">
                    ${{ offer.cost.totalDailyRate }}
                  </h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">
                  Processing Fee<i
                    class="bx bx-help-circle bx-xs help-icon"
                    @click="openTip('Processing Fee', processingFeeContent)"
                  />:
                </div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-normal m-0">
                    ${{ (offer.cost.appliedProcessingFee ? offer.cost.appliedProcessingFee.amount : 0) }}
                  </h5>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-12">
                  <el-collapse accordion>
                    <el-collapse-item name="1">
                      <template slot="title">
                        <div class="col-6">
                          <i class="bx bx-plus mr-1" />
                          <i class="bx bx-minus mr-1" />
                          Location Fees:
                        </div>
                        <div class="col-6 pl-0">
                          <h5 class="font-weight-normal m-0">
                            ${{ offer.cost.totalFees }}
                          </h5>
                        </div>
                      </template>
                      <ul v-if="offer.cost.fees">
                        <li v-for="fee in offer.cost.fees" :key="fee.id">
                          <div class="row align-items-center">
                            <div class="col-6">{{ fee.description }}:</div>
                            <div class="col-6 pl-0">
                              <h5 class="font-weight-normal m-0">
                                ${{ fee.amount }}
                              </h5>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </el-collapse-item>
                  </el-collapse>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-12">
                  <el-collapse accordion>
                    <el-collapse-item name="1">
                      <template slot="title">
                        <div class="col-6">
                          <i class="bx bx-plus mr-1" />
                          <i class="bx bx-minus mr-1" />
                          Discounts:
                        </div>
                        <div class="col-6 pl-0">
                          <h5 class="font-weight-normal m-0">
                            {{ `${offer.cost.totalDiscounts ? '-' : ''} $${offer.cost.totalDiscounts}` }}
                          </h5>
                        </div>
                      </template>
                      <ul v-if="offer.cost.discounts">
                        <li v-for="discount in offer.cost.discounts" :key="discount.id">
                          <div class="row align-items-center">
                            <div class="col-6">{{ discount.description }}:</div>
                            <div class="col-6 pl-0">
                              <h5 class="font-weight-normal m-0">
                                {{ `${discount.amount ? '-' : ''} $${discount.amount}` }}
                              </h5>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </el-collapse-item>
                  </el-collapse>
                </div>
              </div>
              <div class="row align-items-center mb-1">
                <div class="col-6">Total:</div>
                <div class="col-6 pl-0">
                  <h5 class="font-weight-bold m-0">
                    ${{ offer.cost.totalCost }}
                  </h5>
                </div>
              </div>
            </div>
            <div class="row align-items-center mb-1">
              <div class="col-auto">Coupon Code:</div>
              <div class="col pl-0">
                <input
                  :value="couponCode"
                  type="text"
                  placeholder="Coupon Code"
                  class="form-control"
                  addon-left-icon="now-ui-icons users_circle-08"
                  @input="
                    setCouponCode($event.target.value)
                  "
                />
              </div>
              <div class="col-auto">
                <n-button
                  :disabled="!couponCode"
                  native-type="button"
                  type="success"
                  block
                  size="sm"
                  @click.native="applyCouponCode"
                >
                  Apply
                </n-button>
              </div>
            </div>
            <h4 class="mb-2">Customer Info</h4>
            <ValidationObserver v-slot="{ handleSubmit }" ref="reserveForm">
              <form class="form" @submit.prevent="handleSubmit(onReserve)">
                <ValidationProvider name="Email" rules="required|email">
                  <div slot-scope="{ errors }">
                    <label>Email</label>
                    <fg-input
                      :value="customer.email"
                      :readonly="accessToken ? true : false"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons ui-1_email-85"
                      placeholder="Email"
                      @input="setCustomerInfo('email', $event.target.value)"
                    />
                  </div>
                </ValidationProvider>
                <ValidationProvider
                  v-if="!accessToken"
                  :rules="{
                    required: accessToken ? false : true,
                    min: accessToken ? 0 : 6,
                  }"
                  name="Password"
                >
                  <div slot-scope="{ errors }">
                    <label>Password</label>
                    <fg-input
                      :value="customer.password"
                      :error="errors[0]"
                      type="password"
                      addon-left-icon="now-ui-icons ui-1_lock-circle-open"
                      placeholder="Password"
                      @input="setCustomerInfo('password', $event.target.value)"
                    />
                  </div>
                </ValidationProvider>
                <div class="row">
                  <div class="col-md-6">
                    <ValidationProvider name="First Name" rules="required">
                      <div slot-scope="{ errors }">
                        <label>First Name</label>
                        <fg-input
                          :value="customer.firstName"
                          :readonly="accessToken ? true : false"
                          :error="errors[0]"
                          placeholder="First Name"
                          addon-left-icon="now-ui-icons users_circle-08"
                          @input="
                            setCustomerInfo('firstName', $event.target.value)
                          "
                        />
                      </div>
                    </ValidationProvider>
                  </div>
                  <div class="col-md-6">
                    <ValidationProvider name="Last Name" rules="required">
                      <div slot-scope="{ errors }">
                        <label>Last Name</label>
                        <fg-input
                          :value="customer.lastName"
                          :readonly="accessToken ? true : false"
                          :error="errors[0]"
                          placeholder="Last Name"
                          addon-left-icon="now-ui-icons users_circle-08"
                          @input="
                            setCustomerInfo('lastName', $event.target.value)
                          "
                        />
                      </div>
                    </ValidationProvider>
                  </div>
                </div>
                <ValidationProvider
                  :rules="{ required: true, regex: phoneRegex }"
                  name="Phone"
                >
                  <div slot-scope="{ errors }">
                    <label>Phone</label>
                    <fg-input
                      :value="customer.phoneNumber"
                      :readonly="accessToken ? true : false"
                      :error="errors[0]"
                      addon-left-icon="now-ui-icons tech_mobile"
                      placeholder="Phone"
                      @input="setCustomerInfo('phoneNumber', $event.target.value)"
                    />
                  </div>
                </ValidationProvider>
                <h4>
                  Payment
                  <i
                    class="bx bx-help-circle bx-sm help-icon"
                    @click="openTip('Payment Options', paymentOptions)"
                  />
                </h4>
                <!-- <card v-if="validCard">
                  <div class="row align-items-center">
                    <div class="col-auto pr-0">
                      <img
                        :src="'img/card-icons/' + customer.cardsOnFile.cardType.toLowerCase() + '.svg'"
                        class="img cc-icon"
                      />
                    </div>
                    <div class="col-auto cc-number">
                      **** **** ****
                      {{ customer.cardsOnFile.lastFour.substr(4) }}
                    </div>
                    <div class="col cc-expire">
                      Expire: {{ customer.cardsOnFile.expirationDate }}
                    </div>
                  </div>
                </card>
                <card @click.native="openCardModal = true">
                  <div class="row align-items-center">
                    <div class="col-auto pr-0">
                      <img
                        src="img/card-icons/default.svg"
                        class="img cc-icon"
                      />
                    </div>
                    <div class="col-auto cc-number">
                      {{ validCard ? "Change" : "Add" }} Card
                    </div>
                  </div>
                </card> -->
                <div class="card-wrapper">
                  <div>
                    <label>Billing Zip</label>
                    <fg-input
                      :value="zip"
                      :error="errZip"
                      placeholder="Billing Zip"
                      @input="zip=$event.target.value"
                      @blur="validateZip"/>
                  </div>
                  <div>
                    <label>Card Number</label>
                    <fg-input
                      v-cardformat:formatCardNumber
                      ref="cardNumInput"
                      :value="cardNumber"
                      :error="errCardNumber"
                      :autocomplete="'cc-number'"
                      placeholder="#### #### #### ####"
                      @input="cardNumber=$event.target.value"
                      @blur="validateCardNumber">
                      <div 
                        slot="addonLeft" 
                        class="card-icon-wrapper">
                        <img 
                          :src="'img/card-icons/' + getIcon() + '.svg'"
                          height="18">
                      </div>
                    </fg-input>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <label>Expiration</label>
                      <fg-input
                        v-cardformat:formatCardExpiry
                        ref="cardExpInput"
                        :value="cardExpiry"
                        :error="errCardExpiry"
                        :autocomplete="'cc-exp'"
                        placeholder="Expiration"
                        @input="cardExpiry=$event.target.value"
                        @blur="validateCardExpiry"/>
                    </div>
                    <div class="col-6">
                      <label>CVC</label>
                      <fg-input
                        v-cardformat:formatCardCVC
                        ref="cardCvcInput"
                        :value="cardCvc"
                        :error="errCardCvc"
                        :autocomplete="'cc-csc'"
                        placeholder="CVC"
                        @input="cardCvc=$event.target.value"
                        @blur="validateCardCVC"/>
                    </div>
                  </div>
                </div>
                <n-button
                  :disabled="saving"
                  native-type="submit"
                  type="danger"
                  block
                >
                  {{ saving ? "" : "Book Reservation" }}
                  <i v-if="saving" class="bx bx-loader bx-spin" />
                </n-button>
                <img src="/img/authorize-net-seal.png" width="100%" class="my-3">
              </form>
            </ValidationObserver>
          </div>
          <div v-if="offer.reviewSummary" class="row d-block d-md-none">
            <div class="col-12">
              <h4>Reviews</h4>
              <VueSlickCarousel
                :arrows="reviewArrows"
                :as-nav-for="$refs.thumbs"
                :focus-on-select="true"
                :slides-to-show="reviewShows"
              >
                <card
                  v-for="review in offer.reviewSummary.reviews"
                  :key="review.id"
                  type="testimonial"
                  plain
                  @click.native="openReview(review)"
                >
                  <h5 class="card-title">{{ review.reviewerName }}</h5>
                  <star-rating
                    v-model="review.stars"
                    :increment="0.1"
                    :show-rating="false"
                    :star-size="16"
                    :inline="true"
                    :read-only="true"
                  />
                  <p class="card-description">"{{ review.reviewText }}"</p>
                  <div class="review-date">
                    {{ dateFormat(review.reviewDate) }}
                  </div>
                </card>
              </VueSlickCarousel>
            </div>
          </div>
        </div>
      </div>
      <n-button
        v-if="scrollButton"
        type="primary"
        class="btn-scroll"
        @click.native="scrollToReserve"
      >
        Go to Reserve
      </n-button>
    </div>

    <ReviewModal
      :open="openReviewModal"
      :data="review"
      @close="openReviewModal = false"
    />

    <TipModal
      :open="openTipModal"
      :title="tipTitle"
      :content="tipContent"
      @close="openTipModal = false"
    />

    <!-- <CardModal
      :open="openCardModal"
      @close="openCardModal = false"
      @save="setCard"
    /> -->

    <ReceiptModal
      :open="openReceiptModal"
      :data="receipt"
      @close="onCloseReceipt"
    />
  </div>
</template>

<script>
import StarRating from "vue-star-rating";
import VueSlickCarousel from "vue-slick-carousel";
import "vue-slick-carousel/dist/vue-slick-carousel.css";
import "vue-slick-carousel/dist/vue-slick-carousel-theme.css";
import moment from "moment";
import axios from "axios";
import { mapState, mapMutations } from "vuex";
import {
  Collapse as ElCollapse,
  CollapseItem as ElCollapseItem,
  Notification,
} from "element-ui";
import {
  Button,
  Card,
  Collapse,
  CollapseItem,
  DropDown,
  FormGroupInput,
} from "@/components";
import APCDateRangePicker from "@/pages/components/APCDateRangePicker.vue";
import ReviewModal from "@/pages/components/ReviewModal.vue";
import TipModal from "@/pages/components/TipModal.vue";
import CardModal from "@/pages/components/CardModal.vue";
import ReceiptModal from "@/pages/components/ReceiptModal.vue";
import { PHONE_REGEX } from "@/constants";

const PROCESSING_FEE = `
    <ul>
      <li>$5.99 processing fee per transaction</li>
    </ul>
    <p><b>Note:</b> This is just a ONE TIME service fee charge at the time of checkout. No additional reservation fees will be charged to the customer.</p>
  `;
const PAYMENT_OPTIONS = `
    For payment APC does a one time payment and does not store your card on file, so future transactions will require reentering.<br /><br />
    Also note our systems never touch or store your card info we use carefully selected payment processing gateway partners who store, manage, and tokenize your card in the securest way possible to eliminate any possible CC fraud/theft issues in strictest accordance with PCI standards.
  `;
const ZIP_PATTERN = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

export default {
  components: {
    StarRating,
    VueSlickCarousel,
    [ElCollapse.name]: ElCollapse,
    [ElCollapseItem.name]: ElCollapseItem,
    [Button.name]: Button,
    Card,
    Collapse,
    CollapseItem,
    [DropDown.name]: DropDown,
    [FormGroupInput.name]: FormGroupInput,
    APCDateRangePicker,
    ReviewModal,
    TipModal,
    CardModal,
    ReceiptModal,
  },
  props: {
    initialOffer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      processingFeeContent: PROCESSING_FEE,
      paymentOptions: PAYMENT_OPTIONS,
      id: null,
      name: null,
      airportCode: null,
      lat: null,
      lng: null,
      offer: {},
      reviewShows: 3,
      reviewArrows: true,
      openReviewModal: false,
      review: {},
      length: "",
      customer: {},
      openTipModal: false,
      tipTitle: "",
      tipContent: "",
      phoneRegex: PHONE_REGEX,
      cardErr: false,
      openCardModal: false,
      token: null,
      tokenDescription: null,
      saveCardsOnFile: false,
      saving: false,
      openReceiptModal: false,
      receipt: null,
      scrollButton: false,
      dateInfoExample: [
        // { date: "2023-11-03", price: 100.9 },
        // { date: "2023-11-05", price: 98.7 },
        // { date: "2023-11-12", price: 53.9 },
        // { date: "2023-11-14", price: 80.0 },
        // { date: "2023-11-16", price: 125.4 },
      ],
      couponCode: '',
      show: false,
      apiLoginID: '',
      clientKey: '',
      cardBrand: '',
      cardNumber: '',
      errCardNumber: '',
      cardExpiry: '',
      errCardExpiry: '',
      cardCvc: '',
      errCardCvc: '',
      zip: '',
      errZip: '',
      $cardFormat: function() {},
      status: -1
    };
  },
  computed: {
    ...mapState(["accessToken", "member", "loading", "options"]),
    initialRange() {
      return {
        startDate: moment(
          this.offer.startDate + " " + this.offer.startTime
        ).toDate(),
        endDate: moment(this.offer.endDate + " " + this.offer.endTime).toDate(),
      };
    },
    validCard() {
      if (this.cardNumber && this.cardCvc && this.cardExpiry) {
        return true;
      } else {
        return false;
      }
    },
  },
  watch: {
    offer() {
      this.getLength();
    },
    member() {
      this.customer = this.member;
      if (this.$refs.reserveForm) {
        this.$refs.reserveForm.reset();
      }
    },
    open() {
      this.show = this.open;
      setTimeout(() => {
        this.$refs.cardNumInput.$el.querySelector('input').focus();
      }, 0);
    },
    cardNumber(val) {
      if (this.$cardFormat.validateCardNumber(val)) {
        this.$refs.cardExpInput.$el.querySelector('input').focus();
        this.errCardNumber = '';
      } else {
        this.errCardNumber = 'Card number is not valid.';
      }
    },
    cardExpiry(val) {
      if (this.$cardFormat.validateCardExpiry(val)) {
        this.$refs.cardCvcInput.$el.querySelector('input').focus();
        this.errCardExpiry = '';
      } else {
        this.errCardExpiry = 'Expiration is not valid.';
      }
    },
    cardCvc(val) {
      if (this.$cardFormat.validateCardCVC(val)) {
        this.errCardCvc = '';
      } else {
        this.errCardCvc = 'CVC is not valid.';
      }
    },
    zip(val) {
      if (this.validateZip(val)) {
        this.errZip = '';
      } else {
        this.errZip = 'Billing Zip is not valid.';
      }
    }
  },
  created() {
    if (this.accessToken) {
      this.validateToken();
    }
    if (this.$route.query.couponCode) {
      this.couponCode = this.$route.query.couponCode;
      sessionStorage.setItem('couponCode', this.$route.query.couponCode);
    } else if (sessionStorage.getItem('couponCode')) {
      this.couponCode = sessionStorage.getItem('couponCode');
    }
    if (this.$route.query.id) {
      this.id = this.$route.query.id;
    }
    if (this.$route.query.start && this.$route.query.end) {
      this.offer.startDate = this.$route.query.start.split(" ")[0];
      this.offer.startTime = this.$route.query.start.split(" ")[1];

      this.offer.endDate = this.$route.query.end.split(" ")[0];
      this.offer.endTime = this.$route.query.end.split(" ")[1];
    } else {
      const start = moment()
        .add(1, "days")
        .startOf("hour")
        .format("YYYY-MM-DD HH:mm");
      const end = moment()
        .add(5, "days")
        .startOf("hour")
        .format("YYYY-MM-DD HH:mm");

      this.offer.startDate = start.split(" ")[0];
      this.offer.startTime = start.split(" ")[1];

      this.offer.endDate = end.split(" ")[0];
      this.offer.endTime = end.split(" ")[1];
    }
    if (this.$route.query.airportCode) {
      this.airportCode = this.$route.query.airportCode;
    }
    if (this.$route.query.name) {
      this.name = this.$route.query.name;
    }
    if (this.$route.query.lat) {
      this.lat = this.$route.query.lat;
    }
    if (this.$route.query.lng) {
      this.lng = this.$route.query.lng;
    }
    if (!this.initialOffer) {
      this.getOffer();
    } else {
      this.offer = this.initialOffer;
      this.setLoading(false);
    }

    if (window.innerWidth < 576) {
      this.reviewShows = 1;
    } else if (window.innerWidth < 1200) {
      this.reviewShows = 2;
    } else {
      this.reviewShows = 3;
    }

    if (window.innerWidth < 992) {
      this.scrollButton = true;
    } else {
      this.scrollButton = false;
    }
    window.addEventListener("resize", () => {
      if (window.innerWidth < 768) {
        this.reviewShows = 1;
      } else if (window.innerWidth < 1200) {
        this.reviewShows = 2;
      } else {
        this.reviewShows = 3;
      }

      if (window.innerWidth < 992) {
        this.scrollButton = true;
      } else {
        this.scrollButton = false;
      }
    });
    axios.get('paymentgateway').then(res => {
      const { data } = res;
      console.log('paymentgateway: ', data);
      this.apiLoginID = data.authId;
      this.clientKey = data.publicKey;
    });
  },
  methods: {
    ...mapMutations([
      "setLoading",
      "setLoginModal",
      "setAccessToken",
      "setMember",
    ]),
    back() {
      const params = {
        airportCode: this.airportCode,
        start: this.offer.startDate + " " + this.offer.startTime,
        end: this.offer.endDate + " " + this.offer.endTime,
      };
      console.log('back', params);
      this.$router.push({ name: "/offers", query: { ...params } });
    },
    getOffer() {
      const { startDate, startTime, endDate, endTime } = this.offer;
      let query = `apc/offer/update?rateId=${this.id}&startDate=${startDate}&startTime=${startTime}&endDate=${endDate}&endTime=${endTime}`;
      if (sessionStorage.getItem('couponCode')) {
        query += `&couponCode=${sessionStorage.getItem('couponCode')}`;
      }
      axios.get(query).then((res) => {
        const { data } = res;
        console.log("offer: ", data);
        this.offer = data;
        this.setLoading(false);
      });
    },
    validateToken(token) {
      if (token) {
        localStorage.setItem('accessToken', token);
        this.setAccessToken(token);
      }
      setTimeout(() => {
        axios.get("member/validate")
        .then((res) => {
          this.setMember(res.data);
          this.customer = res.data;
          console.log('this.customer', this.customer);
          this.saving = false;
          if (token) {
            this.$router.push({
              name: "/confirmation",
              params: { data: this.receipt },
            });
          }
        })
        .catch((err) => {
          console.error("member/validate: ", err.response);
          this.saving = false;
          localStorage.clear();
          this.setAccessToken(null);
          this.setMember({});
        });
      });
      
    },
    dateFormat(date) {
      return moment(date).format("MM/DD/YYYY");
    },
    openReview(review) {
      setTimeout(() => {
        this.review = review;
        this.openReviewModal = true;
      }, 0);
    },
    getLength() {
      if (this.offer.cost && this.offer.cost.dailyRates) {
        this.length = this.offer.cost.dailyRates.length + ' Days';
      } else {
        this.length = '0 Day';
      }
    },
    updateRange(date) {
      const start = moment(date.startDate).format("YYYY-MM-DD HH:mm");
      const end = moment(date.endDate).format("YYYY-MM-DD HH:mm");
      if (
        start !== this.offer.startDate + " " + this.offer.startTime ||
        end !== this.offer.endDate + " " + this.offer.endTime
      ) {
        console.log("update offer called");
        this.offer.startDate = start.split(" ")[0];
        this.offer.startTime = start.split(" ")[1];
        this.offer.endDate = end.split(" ")[0];
        this.offer.endTime = end.split(" ")[1];
        const query = {
          id: this.offer.rateId,
          start,
          end,
          name: this.name,
          airportCode: this.airportCode,
          lat: this.lat,
          lng: this.lng,
        };
        if (this.lat && this.lng) {
          query["lat"] = this.lat;
          query["lng"] = this.lng;
        }
        this.$router.push({
          name: "/reservation",
          query,
        });

        this.getOffer();
      }
    },
    setCustomerInfo(key, value) {
      this.customer = {
        ...this.customer,
        [key]: value,
      };
    },
    setCouponCode(cp) {
      this.couponCode = cp;
    },
    applyCouponCode() {
      sessionStorage.setItem('couponCode', this.couponCode);
      this.getOffer();
    },
    getIcon() {
      return this.cardBrand === 'unknown' || this.cardBrand === '' ? 'default' : this.cardBrand;
    },
    validateCardNumber() {
      if (!this.cardNumber) {
        this.errCardNumber = 'Card number is required';
      }
    },
    validateCardExpiry() {
      if (!this.cardExpiry) {
        this.errCardExpiry = 'Expiration is required';
      }
    },
    validateCardCVC() {
      if (!this.cardCvc) {
        this.errCardCvc = 'CVC is required';
      }
    },
    validateZip() {
      if (!this.zip) {
        this.errZip = 'Zip is required';
        return false;
      } else if (!ZIP_PATTERN.test(this.zip)) {
        this.errZip = 'Zip is not valid';
        return false;
      }
      return true;
    },
    saveCard(callback) {
      let flag = true;
      if (!this.validateZip()) {
        flag = false;
      }
      if (!this.cardNumber) {
        this.errCardNumber = 'Card number is required';
        flag = false;
      }
      if (!this.cardExpiry) {
        this.errCardExpiry = 'Expiration is required';
        flag = false;
      }
      if (!this.cardCvc) {
        this.errCardCvc = 'CVC is required';
        flag = false;
      }
      console.log('apiLoginID', this.apiLoginID, 'clientKey', this.clientKey)
      if (flag) {
        const month = this.cardExpiry.split('/')[0].trim();
        const year = this.cardExpiry.split('/')[1].trim();
        Accept.dispatchData({
          authData: {
            apiLoginID: this.apiLoginID,
            clientKey: this.clientKey
          },
          cardData: {
            cardNumber: this.cardNumber.replaceAll(' ', ''),
            month,
            year: year.length === 2 ? year : year.substr(2, 2),
            cardCode: this.cardCvc
          }
        }, res => {
          console.log('accept js: ', res);
          if (res.messages.resultCode === 'Ok') {
            callback({
              token: res.opaqueData.dataValue,
              tokenDescription: res.opaqueData.dataDescriptor,
              cardsOnFile: {
                cardType: this.cardBrand,
                lastFour: 'xxxx' + this.cardNumber.replaceAll(' ', '').substr(12),
                expirationDate: this.cardExpiry
              },
            });
          } else {
            this.saving = false;
            Notification.error({
              title: 'Error',
              message: res.messages.message[0].text,
              customClass: 'alert-danger',
              offset: 80,
              showClose: false,
            });
          }
        });
      } else {
        this.saving = false;
      }
    },
    onReserve() {
      // if (!this.validCard) {
      //   this.showNotification("error", "Card Error", "Please add a card");
      //   return;
      // }
      this.saving = true;
      this.saveCard(this.callReserve);
      // this.callReserve();
      // if (this.accessToken) {
      //   this.callReserve();
      // } else {
      //   this.registerMember();
      // }
    },
    callReserve(cardData) {
      const body = {
        rateId: this.offer.rateId,
        token: cardData.token,
        tokenDescription: cardData.tokenDescription,
        startDate: this.offer.startDate,
        startTime: this.offer.startTime,
        endDate: this.offer.endDate,
        endTime: this.offer.endTime,
        email: this.customer.email,
        firstName: this.customer.firstName,
        lastName: this.customer.lastName,
        phone: this.customer.phoneNumber,
      };
      if (!this.accessToken) {
        body['password'] = this.customer.password;
      }
      if (this.saveCardsOnFile) {
        delete body["token"];
        delete body["tokenDescription"];
      }
      if (sessionStorage.getItem('couponCode')) {
        body['couponCode'] = sessionStorage.getItem('couponCode');
      }
      console.log('body', body);
      axios
        .post("apc/reservation", body)
        .then((res) => {
          this.saveCardsOnFile = false;
          // this.openReceiptModal = true;
          this.receipt = res.data.reservation;

          if (!this.accessToken) {
            this.validateToken(res.data.accesstoken);
          } else {
            this.saving = false;
            this.$router.push({
              name: "/confirmation",
              params: { data: this.receipt },
            });
          }
        })
        .catch((err) => {
          const { data } = err.response;
          console.error("reservation: ", data);
          this.showNotification("error", "Failed", data.responseStatus.message);
          this.saving = false;
        });
    },
    onCloseReceipt() {
      this.openReceiptModal = false;
      this.receipt = null;
    },
    // setCard({ token, tokenDescription, cardsOnFile, save }) {
    //   this.token = token;
    //   this.tokenDescription = tokenDescription;
    //   this.saveCardsOnFile = save;
    //   this.customer = {
    //     ...this.customer,
    //     cardsOnFile,
    //   };
    //   if (this.accessToken && save) {
    //     this.saveCard();
    //   }
    // },
    // saveCard(autoReserve = false) {
    //   axios
    //     .post("member/cardonfile", {
    //       noonceToken: this.token,
    //       noonceDescriptor: this.tokenDescription,
    //     })
    //     .then((res) => {
    //       const { data } = res;
    //       console.log("member/cardonfile: ", data);
    //       const member = {
    //         ...this.member,
    //         cardsOnFile: data.cardsOnFile,
    //       };
    //       localStorage.setItem("member", JSON.stringify(member));
    //       this.setMember(member);
    //       if (autoReserve) {
    //         this.callReserve();
    //       }
    //     });
    // },
    registerMember() {
      const body = Object.assign({}, this.customer);
      delete body["cardsOnFile"];
      axios
        .post("member", body)
        .then((res) => {
          const { data } = res;
          this.setMemberData(data);
          if (this.saveCardsOnFile) {
            this.saveCard(true);
          } else {
            this.callReserve();
          }
        })
        .catch((err) => {
          console.error("member: ", err.response);

          axios
            .post("member/login", {
              email: this.customer.email,
              password: this.customer.password,
            })
            .then((res) => {
              const { data } = res;
              this.setMemberData(data);
              if (!this.validCard) {
                this.showNotification(
                  "error",
                  "Card Error",
                  "Please add a card"
                );
                this.saving = false;
              } else {
                this.callReserve();
              }
            })
            .catch((err) => {
              console.error("member/login: ", err.response);
              this.showNotification(
                "error",
                "Login Error",
                "Email exist. Please login with correct password"
              );
              this.saving = false;
            });
        });
    },
    setMemberData(data) {
      localStorage.setItem("accessToken", data.accessToken);
      this.setAccessToken(data.accessToken);
      localStorage.setItem("member", JSON.stringify(data.member));
      this.setMember(data.member);
    },
    showNotification(type, title, message) {
      let customClass = "";
      if (type === "error") {
        customClass = "alert-danger";
      } else if (type === "success") {
        customClass = "alert-success";
      }
      Notification({
        type: type,
        title: title,
        message: message,
        customClass: customClass,
        offset: 80,
        showClose: false,
      });
    },
    scrollToReserve() {
      const el = this.$el.getElementsByClassName("form-reserve")[0];
      el.scrollIntoView();
    },
    openTip(title, content) {
      this.tipTitle = title;
      this.tipContent = content;
      this.openTipModal = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.logo {
  height: 120px;

  @media (max-width: 992px) {
    height: 100px;
  }
}
.carousel-item {
  height: 350px;
  background-image: url("https://cdn.AirportParking.Club/Location/Image/2020/10/23/5f92fd0651302bbe814e10f0.png");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  @media (max-width: 992px) {
    height: 300px;
  }
}
.carousel-thumb-item {
  height: 100px;
  background-image: url("https://cdn.AirportParking.Club/Location/Image/2020/10/23/5f92fd0651302bbe814e10f0.png");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  margin-left: 4px;
  margin-right: 4px;

  @media (max-width: 992px) {
    height: 80px;
  }
}
::v-deep .card-collapse {
  margin-left: 16px;

  .card {
    margin-bottom: 8px;

    .card-header {
      a {
        .bx {
          float: left;
        }
        .bx-plus {
          display: inline-block;
        }
        .bx-minus {
          display: none;
        }
      }
      a[aria-expanded="true"] {
        .bx {
          float: left;
        }
        .bx-plus {
          display: none;
        }
        .bx-minus {
          display: inline-block;
        }
      }
    }
  }
}
p {
  color: #2c2c2c;
  font-size: 1.1em;
}

::v-deep .slick-prev {
  left: 0;
}
::v-deep .slick-next {
  right: 0;
}
::v-deep .slick-prev,
::v-deep .slick-next {
  z-index: 99;
  &::before {
    color: #555357;
  }
}
.card-testimonial {
  margin-top: 0;

  .card-description {
    margin-bottom: 4px;
    white-space: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    // cursor: pointer;
  }
  .review-date {
    text-align: right;
    font-size: 0.8em;
    font-weight: 400;
  }
}

.help-icon {
  position: relative;
  top: 2px;
  margin-left: 2px;
  cursor: pointer;
}

::v-deep .el-collapse {
  border: 0 !important;

  .el-collapse-item__header {
    height: auto !important;
    line-height: unset !important;
    font-size: inherit !important;
    margin-bottom: 4px;
    border: 0 !important;
    margin-left: -15px;
    margin-right: -15px;

    .bx-plus {
      display: inline-block;
    }
    .bx-minus {
      display: none;
    }

    &.is-active {
      .bx-plus {
        display: none;
      }
      .bx-minus {
        display: inline-block;
      }
    }
  }

  .el-collapse-item__wrap {
    border: 0 !important;

    .el-collapse-item__content {
      padding-bottom: 4px;
    }
  }

  .el-collapse-item__arrow {
    display: none;
  }
}

.cc-icon {
  height: 20px;
}
.cc-number {
  font-size: 1.1em;
  font-weight: bold;
}
.cc-expire {
  font-size: 0.9em;
  font-weight: normal;
  text-align: right;
}

.btn-scroll {
  position: fixed;
  bottom: 0;
  right: 16px;
}

.card-wrapper {
  .card-icon-wrapper {
    padding: 10px 0 10px 18px;
    display: inherit;
    border: 1px solid #9A9A9A;
    border-right: 0;
    border-top-left-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
  }

  .input-group-focus {
    .card-icon-wrapper {
      border-color: #f96332;
    }
  }

  .has-danger {
    .card-icon-wrapper {
      border-color: #ffcfcf;
    }
    .form-control {
      border-left: 1px #ffcfcf solid;

      &[placeholder="#### #### #### ####"] {
        border-left: none;
      }
    }
  }
}
</style>
